<template lang="pug">
  .registry-details
    Modal(v-if="modal === 'makePublic'" :header="__('Change Settings')"  @close="modal = null")
      p {{__('In order to share this registry, change its sharing setting to "Public".')}}
      p {{__('Tip: You can add a password if you would like to restrict who can view the registry.')}}
      RogueButton(@click="$emit('edit'); modal = null") {{__('Edit Registry')}}
    share-list-modal(
      :list="list"
      v-if="modal === 'email' || modal === 'copyUrl'"
      :email="modal !== 'copyUrl'"
      :url="url"
      @close="modal = null"
    )
    .registry-details-wrap( :class="{contained: linkPosition === 'inside'}")
      .title-wrap
        h4(v-if="title") {{__(title)}}
        span.list-items(v-if="list?.items.length  > 0") {{`${list?.items.length} items`}}
      .content
        .meta(v-if="details")
          .date {{formatDate(details.date)}}
          .status(v-if="details.isPublic")
            | {{details.hasPassword ? __('Registry Status: Public (with password)') : __('Registry Status: Public') }}
          .status(v-else) {{__('Registry Status: Private') }}
          Tooltip.address(v-if="mine")
            template(#default)
              .icon-flex.address-tip
                | {{__('Registry Address')}}
                Icon(name="info" fill="gray")
            template(#tooltip)
              .tip-contents
                AddressSummary(:address="details.shipping")
                .note {{__('Note: Your address won\'t appear in the shared registry')}}
          .grow
          .sharing(v-if="mine")
            .icon-flex.share(@click="share('copy')")
              Icon(name="share")
              | {{__('Share Registry Link')}}
            .icon-flex.email(@click="share('email')")
              Icon(name="misc/email")
              | {{__('Email Registry')}}
        p.desc(v-if="list") {{__(list.metadata.registry.description)}}
        slot
</template>
<script setup lang="ts">
import { ref, computed } from 'vue'
import { formatDate } from 'Src/js/helpers/date'
import ShareListModal from 'Src/js/components/share-list-modal.vue'
import { useListStore } from '@/js/stores/listStore'
import { useRogue } from '@/js/services/Rogue'

const listStore = useListStore()
const rogue = useRogue()

const props = defineProps({
  list: {
    type    : Object,
    default : null,
  },
  title: {
    type    : String,
    default : null,
  },
  linkPosition: {
    type    : String,
    default : 'top',
  },
})

const modal = ref<string | null>(null)

const registries = computed(() => listStore.registries)

const url = computed(() => `${rogue.localizedBaseURL}/registry/${props.list.metadata.registry.urlKey}`)

// Certain features are disabled / enabled if this is *my* registry, vs if I'm looking at someone elses
const mine = computed(() => registries.value.find(r => r._id === props.list._id))

// Shortcut for registry metadata
const details = computed(() => props.list?.metadata?.registry)

async function share(method: string) {
  const meta = props.list.metadata.registry
  if (!meta.isPublic) {
    modal.value = 'makePublic'
  } else if (method === 'email') {
    modal.value = 'email'
  } else {
    try {
      const result = await rogue.share({ title: meta.name, url: url.value })

      if (result === 'clipboard' || result === 'none') {
        modal.value = 'copyUrl'
      }
    } catch (e) {
      rogue.log.warn('Error sharing wishlist', e)
    }
  }
}
</script>
<style lang="scss" scoped>
.registry-details-wrap {
  border: $border-width-thin solid $color-gray;
  border-radius: $radius-default;
  margin-bottom: $space-s;
}

.date {
  font-family: $font-condensed;
  font-weight: $weight-bold;
  text-transform: uppercase;
}

.date, .status {
  margin-right: $space-m;
}

.title-wrap {
  display: flex;
  flex-direction: row;
  border-bottom: 1px solid #D2D2D2;
  background-color: #F8F8F8;
  padding: $space-xs;

  h4 {
    margin: 0;
  }

  span {
    font-size: $size-xs;
    margin-left: $space-xs;
  }
}

.content {
  padding: $space-xs;
}

.sharing {
  display: flex;
  flex-wrap: nowrap;
  font-weight: $weight-bold;
  font-size: $size-xs;
  cursor: pointer;

  .icon {
    width: $size-xs;
    height: $size-xs;
  }

  .email {
    margin-left: $space-s;
  }
}

.address-tip {
  cursor: pointer;
  font-weight: bold;

  .icon {
    margin-left: $space-xxs
  }
}

.url {
  background-color: $color-gray-light;
  border: 1px solid $color-gray-dark;
  border-radius: $radius-default;
  padding: $space-s;
  font-family: $font-condensed;
}

.meta {
  display: flex;
  align-items: center;
  margin-bottom: $space-m;
  font-size: $size-xs;
  color: $color-black-light;

  .grow {
    flex-grow: 1;
  }
}

.buttons {
  display: flex;
  justify-content: center;

  .button:last-child {
    margin-left: $space-s;
  }
}

.tip-contents {
  font-size: $size-xxs;

  :deep(.address-summary) {
    padding: 0
  }

  .note {
    margin-top: $space-s;
  }
}

.didnt-work {
  display: block;
  margin: $space-s 0 $space-xs 0;
  font-size: $size-xs;
}

@include not-desktop() {
  .registry-details-wrap {
    position: relative;
  }

  .meta {
    position: relative;
  }

  .meta .address {
    display: none;
  }

  .sharing {
    display: block;
    text-wrap: wrap;

    .icon-flex {
      margin: $space-xxxs 0;
    }
  }

  .registry-details-wrap.contained {
    .meta {
      margin-bottom: $space-xxl;
    }
    .sharing {
      top: auto;
      bottom: -$space-l;
    }
  }
}
</style>
